import type { ApiErrorBody } from '@tk/shared';
import { kaanna } from './i18n';

/**
 * Rajapintavirhe, jonka viesti on turvallista näyttää käyttäjälle
 * sellaisenaan: palvelin kirjoittaa sen ja kertoo mitä tehdä. Palvelimen
 * viestit ovat toistaiseksi suomeksi kielivalinnasta riippumatta; vain
 * verkkovirheen oma teksti tulee käyttöliittymän sanastosta.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly field?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // Istunto on httpOnly-evästeessä, joten se on lähetettävä mukana.
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error.code ?? 'INTERNAL_ERROR',
      body?.error.message ?? kaanna('yleinen.yhteysEpaonnistui'),
      body?.error.field,
      body?.error.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
